// Live content store — the public website reads CMS content fetched from
// the backend (single source of truth after migration). The bundled JS data
// remains only as an offline/API-failure fallback; every fallback use is
// logged so it is visible during the transition.
import { useSyncExternalStore } from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const store = {};
let version = 0;
const listeners = new Set();
const settled = new Set();
let started = false;

export function getLive(key) {
  return store[key];
}

// True once the key's fetch has finished — successfully OR after all retries
// failed. Pages use this to stop showing loaders and render the best
// available (possibly bundled fallback) content instead of spinning forever.
export function isContentSettled(key) {
  return settled.has(key);
}

export function setLive(key, value) {
  store[key] = value;
  version += 1;
  listeners.forEach((fn) => fn());
}

function markSettled(key) {
  settled.add(key);
  version += 1;
  listeners.forEach((fn) => fn());
}

export function useLiveTick() {
  return useSyncExternalStore(
    (fn) => { listeners.add(fn); return () => listeners.delete(fn); },
    () => version,
  );
}

// Mobile networks drop requests; retry twice with backoff before giving up.
async function fetchJson(path, retries = 2) {
  let lastErr;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const res = await fetch(`${API}${path}`);
      if (!res.ok) throw new Error(`${path} ${res.status}`);
      return await res.json();
    } catch (err) {
      lastErr = err;
      if (attempt < retries) await new Promise((r) => setTimeout(r, 700 * (attempt + 1)));
    }
  }
  throw lastErr;
}

// One-shot bootstrap of all public content (server-cached endpoints). Each
// key settles independently: a failed fetch marks the key settled so pages
// stop loading and fall back to bundled data rather than spinning forever.
export function initLiveContent() {
  if (started) return;
  started = true;
  const load = (key, path, pick) =>
    fetchJson(path)
      .then((d) => setLive(key, pick(d)))
      .catch((err) => {
        console.warn(`[content] Live CMS fetch failed for ${key}; using bundled fallback data.`, err.message);
      })
      .finally(() => markSettled(key));
  load('bootstrap', '/public/bootstrap', (d) => d);
  load('articles', '/public/articles', (d) => d.articles);
  load('listings', '/public/listings', (d) => d.listings);
  load('caseStudies', '/public/case-studies', (d) => d.caseStudies);
  load('faqs', '/public/faqs', (d) => d.faqs);
  load('testimonials', '/public/testimonials', (d) => d.testimonials);
  load('clientLogos', '/public/client-logos', (d) => d.logos);
  load('timeline', '/public/timeline', (d) => d.milestones);
}
